import { useState } from 'react';
import { Link as RouterLink, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useColorScheme, useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import ContactsIcon from '@mui/icons-material/PeopleAltOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import PipelineIcon from '@mui/icons-material/ViewKanbanOutlined';
import WhatsAppIcon from '@mui/icons-material/ChatOutlined';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import AdsIcon from '@mui/icons-material/AdsClickOutlined';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import LeadsIcon from '@mui/icons-material/MoveToInboxOutlined';
import ProspectsIcon from '@mui/icons-material/TravelExploreOutlined';
import BlogIcon from '@mui/icons-material/ArticleOutlined';
import ReportsIcon from '@mui/icons-material/InsightsOutlined';
import TaskIcon from '@mui/icons-material/TaskAltOutlined';
import FollowUpIcon from '@mui/icons-material/EventRepeatOutlined';
import GroupIcon from '@mui/icons-material/ManageAccountsOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';

import { useAuth } from '@/features/auth/AuthProvider';
import { brand } from '@/theme/theme';

const DRAWER_WIDTH = 248;

interface NavItem {
  label: string;
  to: string;
  icon: typeof DashboardIcon;

  /** Permission required to see the item. */
  permission: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: DashboardIcon, permission: 'dashboard.view' },
  { label: 'Contacts', to: '/contacts', icon: ContactsIcon, permission: 'contacts.view' },
  { label: 'Website leads', to: '/website-leads', icon: LeadsIcon, permission: 'contacts.view' },
  { label: 'Prospects', to: '/prospects', icon: ProspectsIcon, permission: 'prospecting.view' },
  { label: 'Blog', to: '/blog', icon: BlogIcon, permission: 'blog.view' },
  { label: 'Companies', to: '/companies', icon: BusinessIcon, permission: 'companies.view' },
  { label: 'Pipeline', to: '/deals', icon: PipelineIcon, permission: 'deals.view' },
  { label: 'WhatsApp', to: '/whatsapp', icon: WhatsAppIcon, permission: 'whatsapp.view' },
  { label: 'Campaigns', to: '/campaigns', icon: CampaignIcon, permission: 'campaigns.view' },
  { label: 'Ad Studio', to: '/ads', icon: AdsIcon, permission: 'ads.view' },
  { label: 'Web analytics', to: '/web-analytics', icon: InsightsIcon, permission: 'webanalytics.view' },
  { label: 'Reports', to: '/reports', icon: ReportsIcon, permission: 'reports.view' },
  { label: 'Tasks', to: '/tasks', icon: TaskIcon, permission: 'tasks.view' },
  { label: 'Follow-ups', to: '/follow-ups', icon: FollowUpIcon, permission: 'followups.view' },
  { label: 'Users', to: '/users', icon: GroupIcon, permission: 'users.view' },
  { label: 'Settings', to: '/settings', icon: SettingsIcon, permission: 'settings.view' },
];

/** Toggles between light and dark. */
function ThemeToggle() {
  const { mode, setMode } = useColorScheme();

  // `useColorScheme` returns undefined on the very first render while it resolves
  // the stored preference. Rendering a toggle then would show the wrong icon for
  // a frame.
  if (!mode) return null;

  const isDark = mode === 'dark';

  return (
    <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <IconButton onClick={() => setMode(isDark ? 'light' : 'dark')} size="small">
        {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

/** The application logo. */
function Logo() {
  return (
    <Box
      component="img"
      src="/asterlogic-logo.jpeg"
      alt="AsterLogic CRM"
      sx={{ height: 44, width: 'auto', display: 'block' }}
    />
  );
}

/** The persistent application frame: sidebar, top bar, and routed content. */
export function AppShell() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, signOut, can } = useAuth();

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Items the user has no permission for are removed entirely rather than
  // disabled. A greyed-out link only advertises what they cannot reach.
  const visibleNav = NAV_ITEMS.filter((item) => can(item.permission));

  const handleSignOut = async () => {
    setMenuAnchor(null);
    await signOut();
    navigate('/login', { replace: true });
  };

  const drawerContent = (
    <>
      <Toolbar sx={{ px: 2.5 }}>
        <Logo />
      </Toolbar>

      <Divider />

      <List sx={{ px: 1.5, py: 1.5 }}>
        {visibleNav.map(({ label, to, icon: Icon }) => (
          <ListItemButton
            key={to}
            component={NavLink}
            to={to}
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              '&.active': {
                bgcolor: 'action.selected',
                color: 'primary.main',
                fontWeight: 600,
                '& .MuiListItemIcon-root': { color: 'primary.main' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 38 }}>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={label}
              slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 'inherit' } } }}
            />
          </ListItemButton>
        ))}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' } }}
            edge="start"
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flex: 1 }} />

          <ThemeToggle />

          <Tooltip title={user?.email ?? ''}>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} size="small">
              <Avatar sx={{ width: 32, height: 32, background: brand.gradient, fontSize: 14 }}>
                {user?.fullName?.charAt(0).toUpperCase() ?? '?'}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 220 } } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{user?.fullName}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {user?.email}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'primary.main', mt: 0.5 }}>
                {user?.roleName}
              </Typography>
            </Box>

            <Divider />

            <MenuItem component={RouterLink} to="/profile" onClick={() => setMenuAnchor(null)}>
              My profile
            </MenuItem>

            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop || mobileOpen}
          onClose={() => setMobileOpen(false)}
          // Keeps the drawer mounted on mobile so opening it is instant rather
          // than re-rendering the whole nav each time.
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: 'background.default',
          px: { xs: 2, md: 3 },
          py: 3,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
